import Vue from 'vue';
import BaseRepository from './BaseRepository';

class AuthRepository extends BaseRepository {

    /**
     * Log the user in
     *
     * @param {Object} data
     *
     * @returns {Promise}
     */
    login(data) {
        return Vue.http.post(this.route('auth.ajax-login'), data);
    }

    /**
     * Clear all the stored data on the provided instance
     *
     * @param {Object} $vm
     */
    clearStoredUserData($vm) {
        Object.keys($vm.user).forEach((detail) => {
            $vm.$set($vm.user, detail, null);
        });
    }

    /**
     * Log the user out
     *
     * @param {Object} context
     *
     * @returns {Promise}
     */
    logout(context = null) {
        return Vue.http.get(this.route('auth.ajax-logout'))
            .then(({ data }) => {
                if (context) {
                    // Remove stored user data
                    this.clearStoredUserData(context);
                }

                return data;
            });
    }

    /**
     * Perform a basic logout
     *
     * @param {Object} context
     *
     * @returns {Promise}
     */
    basicLogout(context = null) {
        return Vue.http.get(this.route('auth.logout'))
            .then(({ data }) => {
                if (context) {
                    // Remove stored user data
                    this.clearStoredUserData(context);
                }

                return data;
            });
    }

    /**
     * Return the users basic data
     *
     * @param {Object} context - the model to update the user data on
     *
     * @returns {Promise}
     */
    me(context = null) {
        return Vue.http.get(this.route('auth.me'))
            .then(({ data }) => {
                if (context && data.user) {
                    // Update the users data in Vue
                    Object.keys(data.user)
                        .filter((detail) => typeof context.$root.user[detail] !== 'undefined')
                        .forEach((detail) => context.$root.user[detail] = data.user[detail]);
                }

                return data;
            });
    }

    /**
     * Link a social account to an existing YPS user
     *
     * @param {Object} data
     *
     * @returns {Promise}
     */
    linkSocialAccount(data) {
        return Vue.http.post(this.route('api.v2.authenticated.me.social.connect'), data);
    }

    /**
     * Return the users basic data
     *
     * @param {String} service
     * @param {String} token
     * @param {Object} data
     *
     * @returns {Promise}
     */
    socialLogin(service, token, data = {}) {
        return Vue.http.post(this.route('auth.ajax-social-login'), {
            social_provider: service,
            social_token: token,
            ...data,
        });
    }

    /**
     * Checks if the social account is already connected to a user
     *
     * @param {Object} data
     *
     * @returns {Promise}
     */
    checkIfSocialAccountConnected(data) {
        return Vue.http.post(this.route('api.v2.social.check'), data);
    }

    /**
     * Connect the provider to the current user
     *
     * @param {Object} data
     *
     * @returns {Promise}
     */
    connectProvider(data) {
        return Vue.http.post(this.route('api.v2.authenticated.me.social.connect'), data);
    }

    /**
     * Disconnect the provider from the current user
     *
     * @param {Object} data
     *
     * @returns {Promise}
     */
    disconnectProvider(data) {
        return Vue.http.post(this.route('api.v2.authenticated.me.social.disconnect'), data);
    }

    /**
     * Request a password reset email
     *
     * @param {Object} data
     *
     * @returns {Promise}
     */
    forgottenPassword(data) {
        return Vue.http.post(this.route('api.v2.forgotten-password'), data);
    }

    /**
     * Update the users password
     *
     * @param {Object} data
     *
     * @returns {Promise}
     */
    resetPassword(data) {
        return Vue.http.post(this.route('api.v2.reset-password'), data);
    }

    /**
     * Authenticate with a two factor code
     *
     * @param {Object} data
     *
     * @returns {Promise}
     */
    twoFactor(data) {
        return Vue.http.post(this.route('auth.two-factor'), data);
    }

}

export default new AuthRepository;
